import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import { genRecursiveOptions, getRecursiveArray } from '@/utils';
import { z } from 'zod';
import {
  ExpenditureItemNaturePresetFormSchema,
  ExpenditureItemNaturePresetsModel,
  ExpenditureItemNaturesModel,
} from '../models';
import { EXPENDITURE_ITEM_NATURES_CODE_LENGTH, padZeroToLeft } from '../utils';

const ExpenditureItemNaturesModelElement = ExpenditureItemNaturesModel.element;

export const useExpenditureItemNatures = <
  T = Array<z.infer<typeof ExpenditureItemNaturesModelElement>>,
>(
  select?: (data: z.infer<typeof ExpenditureItemNaturesModel>) => T,
) => {
  return useQuery({
    queryKey: ['expenditureItemNatures'],
    queryFn: async () => {
      const data = await axiosGet('/api/expenditure-item-natures/');
      return ExpenditureItemNaturesModel.parse(data);
    },
    select,
  });
};

export const useExpenditureItemNatureOptions = ({
  showCode,
}: {
  showCode?: boolean;
} = {}) => {
  const options = useExpenditureItemNatures((data) =>
    genRecursiveOptions(
      showCode
        ? padZeroToLeft(
            getRecursiveArray(
              data.filter((d) => !d.upperId),
              data,
            ),
            EXPENDITURE_ITEM_NATURES_CODE_LENGTH,
          )
        : getRecursiveArray(
            data.filter((d) => !d.upperId),
            data,
          ),
      'id',
      'name',
    ),
  );
  return options;
};

const ExpenditureItemNaturePresetsModelElement =
  ExpenditureItemNaturePresetsModel.element;

export const useExpenditureItemNaturePresets = <
  T = Array<z.infer<typeof ExpenditureItemNaturePresetsModelElement>>,
>(
  select?: (data: z.infer<typeof ExpenditureItemNaturePresetsModel>) => T,
) => {
  return useQuery({
    queryKey: ['expenditureItemNaturePresets'],
    queryFn: async () => {
      const data = await axiosGet('/api/expenditure-item-nature-presets/');
      return ExpenditureItemNaturePresetsModel.parse(data);
    },
    select,
  });
};

type FormValues = z.infer<typeof ExpenditureItemNaturePresetFormSchema>;

export const useCreateExpenditureItemNaturePreset = () => {
  return useMutation({
    mutationFn: async (values: FormValues) => {
      const { period, expenditureNatureIds, expenditureItemNatureIds } =
        ExpenditureItemNaturePresetFormSchema.parse(values);
      return await axiosPost('/api/expenditure-item-nature-presets/', {
        expenditureNatureId: expenditureNatureIds.at(-1),
        expenditureItemNatureId: expenditureItemNatureIds.at(-1),
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
    },
  });
};

export const useUpdateExpenditureItemNaturePreset = () => {
  return useMutation({
    mutationFn: async (values: FormValues & { id: string }) => {
      const { id, period, expenditureNatureIds, expenditureItemNatureIds } =
        ExpenditureItemNaturePresetFormSchema.extend({
          id: z.string().uuid(),
        }).parse(values);
      return await axiosPatch(`/api/expenditure-item-nature-presets/${id}/`, {
        expenditureNatureId: expenditureNatureIds.at(-1),
        expenditureItemNatureId: expenditureItemNatureIds.at(-1),
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
    },
  });
};

export const useDeleteExpenditureItemNaturePreset = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(
        `/api/expenditure-item-nature-presets/${id}/`,
      );
      await invalidate(['expenditureItemNaturePresets']);
      return res;
    },
  });
};
